// @flow

import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { rem } from 'polished';
import { __ } from '../../../i18n/l10n';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { fontFamily } from '../../../styles/GlobalStyle';
import { InputText } from './InputText';
import { Icon } from './Icon';
import { Modal } from './Modal';
import { CountryCodes } from './CountryCodes';
import config from 'config/app.config';
import { getCountryCallingCodeByLicense } from '../../../utils/CommonUtils';

type InputPhoneRawProps = {
  id: string,
  name: string,
  className?: string,
  register: any,
  error: Object,
  onChange: Function,
  value: string,
};

const InputPhoneRaw = ({
  className,
  id,
  name,
  register,
  error,
  onChange,
  value,
}: InputPhoneRawProps) => {
  const [countryCodesModalIsOpen, setCountryCodesModalIsOpen] = useState(false);
  const [countryCode, setCountryCode] = useState(getCountryCallingCodeByLicense(config.license));
  const [sValue, setValue] = useState(value);
  const inputPhone = useRef<?HTMLInputElement>();

  const openModal = useCallback(() => {
    setCountryCodesModalIsOpen(true);
  }, []);

  const inputFocusEnd = useCallback(() => {
    if (inputPhone.current) {
      const input = inputPhone.current;
      input.focus();
      const inputLength = input.value.length;
      setTimeout(() => {
        input.setSelectionRange(inputLength, inputLength);
      }, 100);
    }
  }, []);

  const countryCodeChanged = useCallback(
    (inputValue: string[]) => {
      setCountryCode(inputValue);
      setCountryCodesModalIsOpen(false);
      setTimeout(() => {
        inputFocusEnd();
      }, 500);
    },
    [inputFocusEnd]
  );

  useEffect(() => {
    if (typeof sValue === 'undefined') return;

    if (onChange) {
      onChange(sValue);
    }
  }, [onChange, sValue]);

  useEffect(() => {
    if (!value) return;

    const phoneNumber = parsePhoneNumberFromString(value);
    if (phoneNumber) {
      setValue(phoneNumber.nationalNumber);
      setCountryCode([phoneNumber.countryCallingCode, phoneNumber.country]);
    } else {
      setValue(value);
    }
  }, [value]);

  return (
    <div className={`${String(className)} inputPhone`}>
      <InputText
        value={sValue}
        name={name}
        id={id}
        type="tel"
        label={__('Phone number')}
        labelOnTop
        ref={(e) => {
          // $FlowFixMe
          inputPhone.current = e;
          register(e);
        }}
        invalid={error}
        onChange={setValue}
      />
      <input
        value={countryCode[1]}
        className="input-country"
        name={`${name}_country`}
        id={`${id}_country`}
        ref={register}
        type="text"
        readOnly
      />
      <div
        className={'inputPhone-countryCode'}
        onClick={openModal}
        role="button"
        onKeyPress={openModal}
      >
        <span>+</span>
        {countryCode[0]}
        <Icon name="icon_arrow_down" />
      </div>
      {/* POPIN COUNTRY CODES */}
      <Modal isOpen={countryCodesModalIsOpen} onClose={setCountryCodesModalIsOpen}>
        <CountryCodes countryCodeChanged={countryCodeChanged} />
      </Modal>
    </div>
  );
};

export const InputPhone: StyledComponent<InputPhoneRawProps, {}, typeof InputPhoneRaw> = styled(
  InputPhoneRaw
)`
  position: relative;

  input {
    transition: all 250ms ease-out;
    padding-left: 55px;
  }

  .inputPhone-countryCode {
    position: absolute;
    top: 39px;
    font-weight: ${fontFamily.primary.black.fontWeight};
    letter-spacing: normal;
    display: flex;
    align-items: center;
    transition: all 250ms ease-out;
    cursor: pointer;
    transform: scale(1);
    user-select: none;
    span {
      font-size: ${rem('20px')};
    }
    svg {
      padding-top: 3px;
    }
  }

  .inputPhone-countryCode--hide {
    opacity: 0;
  }

  .input-country {
    display: none;
  }
`;
